/**
 * Pure helpers for the catch-heatmap layer — port of Flutter's `catch_heat.dart`
 * (`lib/features/home_screen/presentation/layers/catch_heat.dart`). No Angular, no map.
 * Contract: `GET waypoint/heat/?window=&species=` (path is SINGULAR `waypoint/`).
 */

/** Radius of the #1 recommended spot's "search here" zone, nautical miles (backtest-grounded, Flutter). */
export const SEARCH_ZONE_NM = 5;
/** Ring radius for a hotspot whose bundle omits `footprint_nm`. */
export const HOTSPOT_RING_FALLBACK_NM = 1;
export const DEFAULT_WINDOW_DAYS = 14;
/** Below this zoom the blur and rings are not drawn (Flutter `_catchHeatMinZoom`). */
export const CATCH_HEAT_MIN_ZOOM = 6;

const EARTH_RADIUS_NM = 3440.065;
const RING_VERTICES = 64;

export interface CatchSpeciesOption {
  key: string;
  label: string;
  group: string | null;
  count: number | null;
}

/** Shown until the first response lands, or when the backend sends no species rows. */
export const FALLBACK_SPECIES: CatchSpeciesOption[] = [
  { key: 'all', label: 'All species', group: null, count: null },
  { key: 'tuna', label: 'Tuna', group: null, count: null },
  { key: 'billfish', label: 'Billfish', group: null, count: null },
  { key: 'pelagic', label: 'Pelagic', group: null, count: null },
  { key: 'inshore', label: 'Inshore', group: null, count: null },
];

export type GeoFeature = {
  type: 'Feature';
  geometry: { type: string; coordinates: any };
  properties: Record<string, any>;
};

export interface RecommendedSpot {
  lat: number;
  lon: number;
  rank?: number | null;
  confidence?: number | null;
  band?: string | null;
  summary?: string | null;
}

export interface CatchHeatData {
  hotspots: GeoFeature[];
  pins: GeoFeature[];
  /** Individual catches (`kind: "catch"`), the heatmap's weighted points (`w` = qty). */
  density: GeoFeature[];
  regionalSummary: string;
  /** Composed multi-paragraph "Conditions" document, "\n\n"-separated; '' when absent. */
  analysis: string;
  generated: string;
  recommended: RecommendedSpot[];
  window: number;
  availableWindows: number[];
  availableSpecies: CatchSpeciesOption[];
  availableSpeciesByWindow: Record<number, CatchSpeciesOption[]>;
}

export const EMPTY_CATCH_HEAT: CatchHeatData = {
  hotspots: [], pins: [], density: [], regionalSummary: '', analysis: '', generated: '',
  recommended: [], window: DEFAULT_WINDOW_DAYS, availableWindows: [DEFAULT_WINDOW_DAYS],
  availableSpecies: [], availableSpeciesByWindow: {},
};

const asInt = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) ? n : null;
};

/** Defensive row parse — null (row skipped) unless a non-empty string `key` exists. */
function parseSpecies(row: any): CatchSpeciesOption | null {
  if (!row || typeof row !== 'object' || typeof row.key !== 'string' || !row.key) return null;
  return {
    key: row.key,
    label: typeof row.label === 'string' && row.label ? row.label : row.key,
    group: typeof row.group === 'string' && row.group ? row.group : null,
    count: typeof row.count === 'number' ? row.count : null,
  };
}

const parseSpeciesList = (raw: unknown): CatchSpeciesOption[] =>
  (Array.isArray(raw) ? raw : []).map(parseSpecies).filter((o): o is CatchSpeciesOption => !!o);

/** Split one `/heat` FeatureCollection by `properties.kind`; every key may be absent (old backend). */
export function parseCatchHeat(json: any): CatchHeatData {
  const hotspots: GeoFeature[] = [];
  const pins: GeoFeature[] = [];
  const density: GeoFeature[] = [];
  for (const f of Array.isArray(json?.features) ? json.features : []) {
    const kind = f?.properties?.kind;
    if (kind === 'hotspot') hotspots.push(f);
    else if (kind === 'pin') pins.push(f);
    else if (kind === 'catch') density.push(f);
  }
  const top = json?.properties ?? {};
  const recommended: RecommendedSpot[] = (Array.isArray(top.recommended) ? top.recommended : [])
    .filter((r: any) => r && typeof r === 'object' && Number.isFinite(r.lat) && Number.isFinite(r.lon));
  const wins = (Array.isArray(top.available_windows) ? top.available_windows : [])
    .map(asInt).filter((w: number | null): w is number => w != null);
  const byWindow: Record<number, CatchSpeciesOption[]> = {};
  Object.entries(top.available_species_by_window ?? {}).forEach(([k, v]) => {
    const w = asInt(k);
    if (w != null && Array.isArray(v)) byWindow[w] = parseSpeciesList(v);
  });
  return {
    hotspots, pins, density,
    regionalSummary: typeof top.regional_summary === 'string' ? top.regional_summary : '',
    analysis: typeof top.analysis === 'string' ? top.analysis : '',
    generated: typeof top.generated === 'string' ? top.generated : '',
    recommended,
    window: asInt(top.window) ?? DEFAULT_WINDOW_DAYS,
    availableWindows: wins.length ? wins : [DEFAULT_WINDOW_DAYS],
    availableSpecies: parseSpeciesList(top.available_species),
    availableSpeciesByWindow: byWindow,
  };
}

/** Closed GeoJSON polygon: a geodesic circle of `nm` nautical miles around (lat, lon). */
export function circlePolygon(lat: number, lon: number, nm: number): GeoFeature['geometry'] {
  const lat1 = (lat * Math.PI) / 180;
  const lon1 = (lon * Math.PI) / 180;
  const angular = nm / EARTH_RADIUS_NM;
  const ring: number[][] = [];
  for (let i = 0; i <= RING_VERTICES; i++) {
    const bearing = (2 * Math.PI * i) / RING_VERTICES;
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(angular) + Math.cos(lat1) * Math.sin(angular) * Math.cos(bearing));
    const lon2 =
      lon1 +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angular) * Math.cos(lat1),
        Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2),
      );
    ring.push([(lon2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
  }
  return { type: 'Polygon', coordinates: [ring] };
}

/**
 * Today's live pins come back WHOLE regardless of `species` (only hotspots/density are sliced
 * server-side), so this display-time filter keeps pins in step with the blobs. A specific key
 * matches a pin's `category` (category slice) or `subtype` (species slice).
 */
export function visibleCatchPins(pins: GeoFeature[], species: string): GeoFeature[] {
  if (!species || species === 'all') return pins;
  return pins.filter((f) => f?.properties?.['category'] === species || f?.properties?.['subtype'] === species);
}

/** "Heatmap", or "Tuna Heatmap" for a specific slice. */
export function catchHeatTitle(species: string, options: CatchSpeciesOption[]): string {
  if (!species || species === 'all') return 'Heatmap';
  const label = options.find((o) => o.key === species)?.label;
  return label ? `${label} Heatmap` : 'Heatmap';
}

/** Paragraphs of the Conditions document, blank lines dropped. */
export const analysisParagraphs = (analysis: string): string[] =>
  analysis.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
